// src/lib/regulatory.ts: regulator register lookups for manager applications.
//
// The registers are queried through the `regulatory-check` Edge Function rather
// than directly: neither register.fca.org.uk nor efts.sec.gov sends CORS headers
// for third-party origins, so a browser fetch always throws. The function also
// holds the FCA API credentials, which must never reach the client.
import { supabase } from '../services/supabase';

export interface RegulatoryResult {
  found: boolean;
  firmName: string | null;
  status: string | null;
  referenceNumber: string | null;
  regulatorUrl: string | null;
  rawData: any;
  matchConfidence: 'verified' | 'partial' | 'not_found' | 'manual_review' | 'error';
}

export async function checkRegulator(
  jurisdiction: string,
  number: string,
): Promise<RegulatoryResult> {
  try {
    const { data, error } = await supabase.functions.invoke('regulatory-check', {
      body: { jurisdiction, number },
    });
    if (error) throw error;
    if (!data || typeof (data as any).matchConfidence !== 'string') {
      throw new Error('Unexpected response from regulatory-check');
    }
    return data as RegulatoryResult;
  } catch {
    return {
      found: false, firmName: null, status: null,
      referenceNumber: null, regulatorUrl: null,
      rawData: null, matchConfidence: 'error',
    };
  }
}

// Loose comparison between what the applicant typed and what the register
// returned, so the admin sees an obvious mismatch at a glance.
export function firmNamesMatch(applicantName: string | null, regulatorName: string | null): boolean {
  if (!applicantName || !regulatorName) return false;
  const norm = (s: string) => s.toLowerCase()
    .replace(/\b(limited|ltd|llp|llc|inc|plc|lp|partners|capital|management|advisors|advisers)\b/g, '')
    .replace(/[^a-z0-9]/g, '');
  const a = norm(applicantName);
  const b = norm(regulatorName);
  return !!a && !!b && (a === b || a.includes(b) || b.includes(a));
}
